import { useState } from 'react';
import { downloadCsv, useGeneralLedger } from '../hooks/useReports';
import { useAccounts, accountCategoryLabels } from '../hooks/useAccounts';
import PrintButton from '../components/PrintButton';

export default function GeneralLedgerPage() {
  const today = new Date();
  const yearStart = `${today.getFullYear()}-01-01`;
  const yearEnd = `${today.getFullYear()}-12-31`;

  const { data: accounts } = useAccounts();
  const [accountId, setAccountId] = useState('');
  const [dateFrom, setDateFrom] = useState(yearStart);
  const [dateTo, setDateTo] = useState(yearEnd);
  const [applied, setApplied] = useState<{
    accountId: string;
    dateFrom: string;
    dateTo: string;
  } | null>(null);

  const { data, isLoading, error } = useGeneralLedger(applied?.accountId, {
    dateFrom: applied?.dateFrom,
    dateTo: applied?.dateTo,
  });

  return (
    <div>
      <div className="page-header">
        <h2>総勘定元帳</h2>
      </div>

      {error && <div className="error-banner">{(error as Error).message}</div>}

      <div className="card">
        <div className="toolbar">
          <label>
            勘定科目{' '}
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">選択してください</option>
              {accounts?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} {a.name}（{accountCategoryLabels[a.category]}）
                </option>
              ))}
            </select>
          </label>
          <label>
            日付From{' '}
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label>
            日付To{' '}
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
          <button
            className="primary"
            onClick={() => accountId && setApplied({ accountId, dateFrom, dateTo })}
            disabled={!accountId}
          >
            表示
          </button>
          <button
            onClick={() => applied && downloadCsv('/api/reports/general-ledger', applied)}
            disabled={!applied}
          >
            CSV出力
          </button>
          <PrintButton />
        </div>
      </div>

      {isLoading && <div>読み込み中...</div>}

      {data && (
        <>
          <div className="card">
            <strong>
              {data.account.code} {data.account.name}
            </strong>
            <span className="text-muted" style={{ marginLeft: 12 }}>
              （{accountCategoryLabels[data.account.category as keyof typeof accountCategoryLabels]}）
            </span>
            <span style={{ marginLeft: 16 }}>
              期末残高:{' '}
              <strong className="numeric" style={{ fontSize: 16 }}>
                {data.finalBalance.toLocaleString()} 円
              </strong>
            </span>
          </div>

          <table>
            <thead>
              <tr>
                <th style={{ width: 110 }}>取引日付</th>
                <th>摘要</th>
                <th className="numeric" style={{ width: 130 }}>借方</th>
                <th className="numeric" style={{ width: 130 }}>貸方</th>
                <th className="numeric" style={{ width: 130 }}>残高</th>
                <th style={{ width: 80 }}>状態</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((l, i) => (
                <tr key={`${l.entryId}-${i}`}>
                  <td>{l.entryDate}</td>
                  <td>{l.description}</td>
                  <td className="numeric">
                    {l.debitAmount > 0 ? l.debitAmount.toLocaleString() : ''}
                  </td>
                  <td className="numeric">
                    {l.creditAmount > 0 ? l.creditAmount.toLocaleString() : ''}
                  </td>
                  <td className="numeric">{l.balance.toLocaleString()}</td>
                  <td>
                    {l.status === 'active' ? (
                      <span className="badge active">有効</span>
                    ) : (
                      <span className="badge voided">取消</span>
                    )}
                  </td>
                </tr>
              ))}
              {data.lines.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 24 }} className="text-muted">
                    取引がありません
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
